/**
 * Configurazione dei tenant per l'applicazione client
 * 
 * IMPORTANTE: Questa configurazione è utilizzata solo in modalità di sviluppo
 * In produzione, le informazioni sui tenant vengono caricate dinamicamente dal backend
 */

// Tipologie di tenant nel sistema
export enum TenantType {
    RESTAURANT = 'restaurant',
    CAFE = 'cafe',
    BAR = 'bar',
    PIZZERIA = 'pizzeria',
    CUSTOM = 'custom'
}

// Struttura dei dati specifici di ogni tenant
export interface TenantConfig {
    id: string;
    name: string;
    type: TenantType;
    logo?: string;
    description?: string;
    primaryColor: string;
    secondaryColor: string;
    backgroundColor: string;
    textColor: string;
    fontFamily: string;
    // Menu PDF specifici per tenant
    menuPdfs?: {
        name: string;
        file: string;
    }[];
    // Impostazioni aggiuntive
    settings?: {
        showLogo: boolean;
        showSocialLinks: boolean;
        enableOnlineOrders: boolean;
        enableReservations: boolean;
        currencySymbol: string;
        dateFormat: string;
        timeFormat: string;
    };
}

/**
 * Lista dei tenant conosciuti per il selettore di tenant
 * Usata solo in ambiente di sviluppo per facilitare la selezione
 */
export const KNOWN_TENANTS = [
    {
        id: '1',
        name: 'Demo Restaurant',
        type: TenantType.RESTAURANT,
        description: 'Tenant di esempio per dimostrazioni',
        isActive: true
    },
    {
        id: '2',
        name: "Pep's Cibi & Vino",
        type: TenantType.RESTAURANT,
        description: 'Ristorante italiano',
        isActive: true
    },
    {
        id: '3',
        name: 'Caffetteria Milano',
        type: TenantType.CAFE,
        description: 'Caffetteria',
        isActive: true
    },
    {
        id: '4',
        name: 'Ristorante Sapori',
        type: TenantType.RESTAURANT,
        description: 'Ristorante tradizionale',
        isActive: true
    }
];

/**
 * Configurazioni di default per tenant specifici (solo per lo sviluppo)
 * In produzione queste vengono caricate dal backend
 */
export const DEFAULT_TENANT_CONFIGS: Record<string, TenantConfig> = {
    '1': {
        id: '1',
        name: 'Demo Restaurant',
        type: TenantType.RESTAURANT,
        description: 'Tenant di esempio per dimostrazioni',
        primaryColor: '#2196f3',
        secondaryColor: '#f50057',
        backgroundColor: '#ffffff',
        textColor: '#333333',
        fontFamily: 'Roboto, sans-serif',
        settings: {
            showLogo: true,
            showSocialLinks: true,
            enableOnlineOrders: true,
            enableReservations: true,
            currencySymbol: '€',
            dateFormat: 'DD/MM/YYYY',
            timeFormat: '24h'
        }
    },
    '2': {
        id: '2',
        name: "Pep's Cibi & Vino",
        type: TenantType.RESTAURANT,
        description: 'Ristorante italiano',
        logo: '/tenant-assets/assets/peps/logo.png',
        primaryColor: '#9c27b0',
        secondaryColor: '#ff9800',
        backgroundColor: '#f5f5f5',
        textColor: '#212121',
        fontFamily: 'Playfair Display, serif',
        menuPdfs: [
            { name: "Pranzo", file: "/tenant-assets/assets/peps/menu-pranzo.pdf" },
            { name: "Colazione", file: "/tenant-assets/assets/peps/menu-colazione.pdf" },
            { name: "Cena", file: "/tenant-assets/assets/peps/menu-cena.pdf" }
        ],
        settings: {
            showLogo: true,
            showSocialLinks: true,
            enableOnlineOrders: true,
            enableReservations: true,
            currencySymbol: '€',
            dateFormat: 'DD/MM/YYYY',
            timeFormat: '24h'
        }
    },
    '3': {
        id: '3',
        name: 'Caffetteria Milano',
        type: TenantType.CAFE,
        description: 'Caffetteria',
        logo: '/tenant-assets/assets/caffetteria.jpg',
        primaryColor: '#795548',
        secondaryColor: '#4caf50',
        backgroundColor: '#f8f5f2',
        textColor: '#3e2723',
        fontFamily: 'Montserrat, sans-serif',
        menuPdfs: [
            { name: "Menu Completo", file: "/tenant-assets/assets/3/menu-completo.pdf" }
        ],
        settings: {
            showLogo: true,
            showSocialLinks: true,
            enableOnlineOrders: true,
            enableReservations: false,
            currencySymbol: '€',
            dateFormat: 'DD/MM/YYYY',
            timeFormat: '24h'
        }
    },
    '4': {
        id: '4',
        name: 'Ristorante Sapori',
        type: TenantType.RESTAURANT,
        description: 'Ristorante tradizionale',
        primaryColor: '#e91e63',
        secondaryColor: '#ffc107',
        backgroundColor: '#ffffff',
        textColor: '#333333',
        fontFamily: 'Lora, serif',
        menuPdfs: [
            { name: "Menu Completo", file: "/tenant-assets/assets/4/menu-completo.pdf" }
        ],
        settings: {
            showLogo: true,
            showSocialLinks: true,
            enableOnlineOrders: true,
            enableReservations: true,
            currencySymbol: '€',
            dateFormat: 'DD/MM/YYYY',
            timeFormat: '24h'
        }
    }
};

/**
 * Ottiene la configurazione per il tenant specificato
 * Usa la configurazione di default se non è disponibile dal backend
 */
export const getTenantConfig = (tenantId: string): TenantConfig | null => {
    if (!tenantId) return null;
    return DEFAULT_TENANT_CONFIGS[tenantId] || null;
};

/**
 * Ottiene i file di menu PDF per il tenant specificato
 */
export const getTenantMenuPdfs = (tenantId: string) => {
    const config = getTenantConfig(tenantId);
    if (!config || !config.menuPdfs || config.menuPdfs.length === 0) {
        // Fornisci un PDF di default generico
        return [{
            name: "Menu Completo",
            file: `/tenant-assets/assets/${tenantId || 'default'}/menu-completo.pdf`
        }];
    }
    return config.menuPdfs;
};

export default {
    DEFAULT_TENANT_CONFIGS,
    getTenantConfig,
    getTenantMenuPdfs
};
